import React, { useState } from "react";
import { Box, Typography, Button, Chip } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PetsIcon from "@mui/icons-material/Pets";
import type { Dog } from "../types";
import { QuizStyledCard } from "../components/shared/QuizStyledCard";
import { DeleteDogDialog } from "../components/DogPage/DeleteDogDialog";
import {
  PersonalityBadgesRow,
  PlanSnapshotSection,
  BasicsSection,
  PhysicalSection,
  HealthSection,
} from "../components/DogPage/DogProfileSections";
import { QUIZ } from "@/theme/quizTokens";

export function DogProfilePage({
  dog,
  onBack,
  onEdit,
  onDelete,
  onRestore,
  canDelete = true,
}: {
  dog: Dog | undefined;
  onBack: () => void;
  onEdit: (dogId: string) => void;
  onDelete?: (dogId: string) => void;
  onRestore?: (dogId: string) => void;
  canDelete?: boolean;
}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  if (!dog) {
    return (
      <QuizStyledCard>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={onBack}
          sx={{ mb: 2, textTransform: "none", fontWeight: 700, color: QUIZ.color.brown }}
        >
          Back to Dogs
        </Button>
        <Typography sx={{ textAlign: "center", py: 4, color: QUIZ.color.brown, opacity: 0.6 }}>
          Dog not found.
        </Typography>
      </QuizStyledCard>
    );
  }

  const isDeleted = !!dog.deleted;
  const sexLabel = dog.sex === "MALE" ? "Male" : dog.sex === "FEMALE" ? "Female" : dog.sex || "";
  const subtitle = [dog.breed, sexLabel].filter(Boolean).join(" \u00B7 ");

  return (
    <Box sx={{ maxWidth: 680, mx: "auto" }}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={onBack}
        sx={{
          mb: 2,
          textTransform: "none",
          fontWeight: 700,
          color: QUIZ.color.brown,
          "&:hover": { bgcolor: "rgba(255,225,221,0.3)" },
        }}
      >
        Back to Dogs
      </Button>

      <QuizStyledCard>
        {/* Removed notice */}
        {isDeleted && (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #FFE5E2 0%, #FFF0EE 100%)",
              border: "1px dashed #FFCCC7",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <Chip
                label="Removed"
                size="small"
                sx={{ height: 24, fontSize: 11, fontWeight: 800, backgroundColor: "#D14C3F", color: QUIZ.color.white }}
              />
            </Box>
            <Typography sx={{ fontSize: 13, color: QUIZ.color.brown, lineHeight: 1.5 }}>
              Our team will review and delete this profile. If this was a mistake, you can restore {dog.name || "them"}.
            </Typography>
          </Box>
        )}

        {/* Header: avatar + name + actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 2.5,
            flexDirection: { xs: "column", sm: "row" },
            ...(isDeleted ? { opacity: 0.5, filter: "grayscale(60%)" } : {}),
          }}
        >
          <Box
            sx={{
              width: { xs: 64, md: 72 },
              height: { xs: 64, md: 72 },
              borderRadius: "18px",
              background: "linear-gradient(135deg, #E9DFD7 0%, #FFD1CC 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 4px 12px rgba(123, 51, 6, 0.15)",
            }}
          >
            <PetsIcon sx={{ fontSize: { xs: 32, md: 36 }, color: QUIZ.color.brown }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: 24, md: 28 },
                fontWeight: 800,
                color: QUIZ.color.brown,
                lineHeight: 1.2,
              }}
            >
              {dog.name || "Unnamed Dog"}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 14, color: QUIZ.color.brown, opacity: 0.65, fontWeight: 600, mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
          {!isDeleted && (
            <Button
              variant="contained"
              startIcon={<EditRoundedIcon />}
              onClick={() => onEdit(String(dog.id))}
              sx={{
                flex: 1,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 800,
                fontSize: 14,
                py: 1.25,
                bgcolor: QUIZ.color.brown,
                "&:hover": { bgcolor: "#5C2504" },
              }}
            >
              Edit Profile
            </Button>
          )}
          {!isDeleted && onDelete && canDelete && (
            <Button
              variant="outlined"
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 14,
                py: 1.25,
                borderColor: "rgba(211,76,63,0.3)",
                color: "#D14C3F",
                "&:hover": { borderColor: "#D14C3F", bgcolor: "rgba(255,229,226,0.3)" },
              }}
            >
              Remove
            </Button>
          )}
          {isDeleted && onRestore && (
            <Button
              fullWidth
              variant="contained"
              onClick={() => onRestore(String(dog.id))}
              sx={{
                borderRadius: "12px",
                py: 1.25,
                background: "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
                color: QUIZ.color.white,
                fontWeight: 800,
                fontSize: 14,
                textTransform: "none",
                "&:hover": { background: "linear-gradient(135deg, #43A047 0%, #5CB85C 100%)" },
              }}
            >
              Restore {dog.name || "Dog"}
            </Button>
          )}
        </Box>

        {/* Personality badges */}
        <Box sx={isDeleted ? { opacity: 0.5, filter: "grayscale(60%)" } : {}}>
          <PersonalityBadgesRow dog={dog} />

          {/* Sections */}
          <PlanSnapshotSection dog={dog} />
          <BasicsSection dog={dog} />
          <PhysicalSection dog={dog} />
          <HealthSection dog={dog} />
        </Box>
      </QuizStyledCard>

      <DeleteDogDialog
        open={deleteDialogOpen}
        dogName={dog.name || "this dog"}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          setDeleteDialogOpen(false);
          onDelete?.(String(dog.id));
        }}
      />
    </Box>
  );
}
